import UrlService from 'common/services/Url';

/**
 * Formateurs d'affichage.
 *
 * La maquette mélange les conventions d'une page à l'autre : « 4.6/5 » et
 * « 1.2 km » sur le listing, « 4,6 » et « 1,2 km » sur le détail (piège n°3).
 * Convention FR retenue : virgule décimale partout.
 */

/**
 * Lien vers une fiche salon.
 *
 * `merchant.localpageUrl` arrive de l'API en **absolu** et en `http://`
 * (`places_manager.js` le code en dur côté serveur, quel que soit
 * l'environnement). Deux conséquences :
 *  - le passer à `<Link to>` de react-router le traite comme un chemin et le
 *    concatène à l'origine courante → `https://host/http://host/…`, soit un
 *    domaine en double et un lien mort ;
 *  - le laisser en `http://` sur un site servi en https coûte une redirection.
 *
 * On aligne donc le protocole sur celui de la page courante (`common/services/Url`)
 * plutôt que de forcer `https://` en dur : forcer https cassait le lien en
 * staging, où l'host de la fiche salon n'a pas de certificat TLS valide — VOG
 * n'a pas ce problème car il ne touche pas à `localpageUrl`. On sert un
 * `<a href>` (une fiche salon est une page SSR à part entière, comme dans
 * 2025_VOG : une navigation côté client arriverait sur `LocalPage` avec l'état
 * du listing). Les chemins relatifs — `mockState.js` en dev — sont laissés tels quels.
 *
 * Ce rechargement complet perd `state.searchOrigin` : on porte donc le point de
 * référence en query params (`fromLat`/`fromLong`, mêmes noms que `getBrands`)
 * pour que le backend puisse calculer `merchant.distance` côté SSR de la fiche
 * salon (`routes/api_handler.js:_v1_fetchSinglePlace`), avec le même `geoNear`
 * que le listing plutôt qu'un recalcul en JS côté client.
 */
export const localpageHref = (url, origin) => {
  if (!url) return '';
  const wantedProtocol = typeof window !== 'undefined' ? `${window.location.protocol}//` : 'https://';
  const httpsUrl = /^https?:\/\//i.test(url) ? UrlService.modifyHTTP(url, wantedProtocol) : url;
  if (!origin || origin.lat == null || origin.lng == null) return httpsUrl;
  // `URLSearchParams` fusionne avec une éventuelle query string déjà présente
  // sur `localpageUrl` plutôt que de l'écraser en concaténant à la main.
  try {
    const parsed = new URL(httpsUrl);
    parsed.searchParams.set('fromLat', origin.lat);
    parsed.searchParams.set('fromLong', origin.lng);
    return parsed.toString();
  } catch (error) {
    // Chemin relatif (`mockState.js` en dev) : pas d'origine à résoudre pour `URL`.
    const separator = httpsUrl.includes('?') ? '&' : '?';
    return `${httpsUrl}${separator}fromLat=${origin.lat}&fromLong=${origin.lng}`;
  }
};

/**
 * Repli commun pour comparer une ville à un raccourci de recherche : minuscules,
 * accents retirés, tout ce qui n'est pas alphanumérique replié en tiret. C'est
 * ce qui fait tomber « Saint-Étienne », « saint etienne » et le slug
 * « saint-etienne » sur la même clé.
 *
 * `String.prototype.normalize` est absent d'IE11 ; sur un navigateur qui ne
 * l'a pas, on perd juste la dépliage des accents — la comparaison reste bonne
 * pour les villes sans accent, et le fil d'ariane retombe proprement sur le
 * listing général pour les autres.
 */
const normalizeCity = (value) => {
  const text = String(value || '').toLowerCase();
  const withoutAccents = text.normalize ? text.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : text;
  return withoutAccents.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
};

/**
 * Cible du deuxième niveau du fil d'ariane (spec 3.a).
 *
 * La spec demande un lien vers la page listing du **département** du salon,
 * « si possible », sinon vers le listing général. Le département n'existe nulle
 * part dans la donnée (question ouverte, §18 du plan) — la **ville**, elle, est
 * une page réelle dès qu'un `search_shortcut` la déclare : `/location/<slug>`,
 * la même URL que celle des liens du bloc villes.
 *
 * `state.lp.search_shortcuts` est bien marshallé sur la fiche salon, pas
 * seulement sur le listing (`api_handler.js:457`, `AntiCorruption.js:480`), et
 * chaque raccourci porte son `slugUrl` persisté — aucun appel serveur à ajouter.
 *
 * Renvoie `''` quand aucun raccourci ne correspond : c'est à l'appelant de
 * retomber sur le listing général, avec un libellé qui décrit la cible réelle
 * plutôt qu'une ville.
 */
export const cityShortcutHref = (city, shortcuts) => {
  const target = normalizeCity(city);
  if (!target) return '';
  const match = (shortcuts || []).find(
    (shortcut) =>
      normalizeCity(shortcut.slug) === target ||
      normalizeCity(shortcut.value) === target ||
      normalizeCity(shortcut.label) === target,
  );
  return (match && match.slugUrl) || '';
};

/** 4.6 → « 4,6 » ; null → '' */
export const decimalFr = (value, digits = 1) => {
  const number = parseFloat(value);
  if (isNaN(number)) return '';
  return number.toFixed(digits).replace('.', ',');
};

/** 1.234 → « 1,2 km » ; en deçà de 1 km on bascule en mètres. */
export const distanceFr = (kilometers) => {
  const number = parseFloat(kilometers);
  if (isNaN(number)) return '';
  if (number < 1) return `${Math.round(number * 1000)} m`;
  return `${decimalFr(number)} km`;
};

/** 892 → « 892 avis » ; 1 → « 1 avis ». */
export const reviewCountFr = (count) => {
  const number = parseInt(count, 10);
  if (isNaN(number) || number <= 0) return '';
  return `${number.toLocaleString('fr-FR')} avis`;
};

/**
 * Libellé de la puce de distance (décision 9). Le texte dépend de l'origine du
 * point de référence : sans point de référence, la puce n'existe pas.
 */
export const distanceLabel = (kilometers, origin) => {
  const value = distanceFr(kilometers);
  if (!value) return '';
  if (origin && origin.kind === 'user') return `À ${value} de vous`;
  if (origin && origin.kind === 'city' && origin.label) {
    return `À ${value} du centre de ${origin.label}`;
  }
  return value;
};

/**
 * '09:30' → « 9h30 » ; '19:00' → « 19h ».
 *
 * `DatesService.formatHour` applique la même règle mais garde le zéro de tête
 * (« 09h30 »). On ne le corrige pas dans `common` : ça changerait le rendu de
 * 2025_VOG, ce que le corollaire de la décision #14 interdit. La maquette Tchip
 * écrit « 9h30–19h30 », donc le zéro tombe ici.
 */
export const hourFr = (hour) => {
  if (!hour) return '';
  const formatted = String(hour).replace(':', 'h');
  const withoutTrailingZeros = formatted.endsWith('00') ? formatted.slice(0, -2) : formatted;
  return withoutTrailingZeros.replace(/^0(?=.)/, '');
};

/** '09:30'–'19:30' → « 9h30–19h30 ». */
export const hourRangeFr = (begin, end) => `${hourFr(begin)}–${hourFr(end)}`;

/**
 * « Tchip Coiffure Paris Bastille » → ["Tchip Coiffure", "Paris Bastille"].
 *
 * La maquette coupe toujours le nom du salon après l'enseigne, sur deux lignes
 * (`.salon-row-top h2`, `.salon-panel h1`). Le nom du salon n'est pas structuré
 * en base — c'est une chaîne unique — donc on coupe sur le préfixe connu de
 * l'enseigne plutôt que sur le dernier mot, qui casserait "Paris Bastille" en
 * "Paris" / "Bastille".
 */
export const BRAND_PREFIX = 'Tchip Coiffure';

export const splitSalonName = (name) => {
  const value = String(name || '');
  if (value.toLowerCase().startsWith(BRAND_PREFIX.toLowerCase())) {
    const rest = value.slice(BRAND_PREFIX.length).trim();
    return rest ? [BRAND_PREFIX, rest] : [BRAND_PREFIX];
  }
  return [value];
};
